import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  availableClientTools,
  isVerbatimExcerpt,
  type AgentChatConfig,
  type AgentChatResponse,
  type AgentMode,
  type ChatResponse,
  type ClientAction,
  type ReqGuardAction,
  type ReqGuardChatResponse,
} from '@cypremos/shared'
import { apiFetch, isGuest } from '@/lib/apiFetch'
import type { Message } from '../ChatPanel'
import { useChatController } from '../useChatController'
import { sendDocsChatTurn, useRequirementAuditLaunch } from '../useDocsChat'
import { AgentBlocks } from './AgentBlocks'
import { useChatActionBridge, type WizardBridgeHandle } from './chatActionBridgeContext'
import { selectedAssumptionCount, type ChatBlock, type ConfirmGate } from './types'
import {
  bindConfirm,
  isConfirmStale,
  isReqGuardResponse,
  precheckReqGuardAction,
  reqGuardResponseBlocks,
  runReqGuardAction,
  sendReqGuardTurn,
} from './reqGuardChat'

type Turn = { role: 'user' | 'assistant'; content: string }
type AnyResponse = ChatResponse | AgentChatResponse | ReqGuardChatResponse

function isAgentResponse(response: AnyResponse): response is AgentChatResponse {
  return 'actions' in response && !isReqGuardResponse(response)
}

/** The model may only *offer* these steps; each becomes a confirm card a human presses. */
const PROPOSAL_GATES: Partial<Record<ClientAction['name'], ConfirmGate>> = {
  propose_test_generation: 'testCases',
  propose_robot_generation: 'robot',
}

/** The wizard step a confirm card is valid on. */
const GATE_STEP: Record<ConfirmGate, string> = {
  testCases: 'review_classification',
  robot: 'review_test_cases',
}

/**
 * The floating Chat's controller. On pages where the vECU test generator wizard is
 * mounted it talks to /agent-chat and can drive the wizard; on the Requirement Doctor
 * page it talks to /req-doctor/agent-chat and drives that page (see reqGuardChat.ts);
 * everywhere else it is the unchanged docs chat (/docs-chat + "Open Requirement Audit" CTA). The switch is made
 * per turn, so one conversation continues across navigation.
 *
 * Human gates enforced here (in addition to the server):
 *  - after classification, and again after test-case review, only a confirm card's
 *    button (or the wizard's own button) moves on; the AI never judges a step done;
 *  - test execution is never reachable — the bridge does not expose it;
 *  - requirements from chat are re-checked to be a verbatim excerpt before use.
 */
export function useAssistantChat(currentPath?: string) {
  const { t } = useTranslation()
  const bridge = useChatActionBridge()
  const { handleIntent, ctaSlot } = useRequirementAuditLaunch()
  const [forceMock, setForceMock] = useState(false)
  const [actionRunning, setActionRunning] = useState(false)
  const messagesRef = useRef<Message[]>([])

  useEffect(() => {
    let cancelled = false
    apiFetch('/agent-chat/config')
      .then((res) => (res.ok ? res.json() : null))
      .then((config: AgentChatConfig | null) => {
        if (!cancelled && config) setForceMock(config.forceMock)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const reqGuardActive = !bridge?.wizardAttached && !!bridge?.reqGuardAttached
  const agentActive = !!bridge?.wizardAttached || reqGuardActive
  const serverForcesMock = forceMock || isGuest()
  // Docs chat always requests Live; the agent chats follow their page's Mock/Live toggle.
  const pageUseMock = reqGuardActive ? bridge?.reqGuardUseMock : bridge?.wizardUseMock
  const mode: AgentMode = serverForcesMock || (agentActive && pageUseMock) ? 'mock' : 'live'
  const modeRef = useRef(mode)
  modeRef.current = mode

  const sendTurn = useCallback(async (messages: Turn[]): Promise<AnyResponse> => {
    const wizard = bridge?.getWizard()
    if (!wizard) {
      const page = bridge?.getReqGuard()
      if (page) return sendReqGuardTurn(page, messages, bridge?.reqGuardUseMock ?? true, currentPath, t)
      return sendDocsChatTurn(messages, currentPath)
    }
    const response = await apiFetch('/agent-chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages,
        snapshot: wizard.getSnapshot(),
        useMock: bridge?.wizardUseMock ?? true,
        currentPath,
      }),
    })
    if (!response.ok) {
      // Most often Live mode without AWS credentials — say so instead of a generic failure.
      const body = await response.json().catch(() => null) as { message?: string; mode?: AgentMode } | null
      if (!body) throw new Error(`HTTP error! status: ${response.status}`)
      const failed: AgentChatResponse = {
        message: t('chat.agent.error.backend', { error: body.message ?? response.status }),
        intent: { type: 'none' },
        mode: body.mode ?? modeRef.current,
        actions: [],
        rejectedActions: [],
        cards: [],
        usedDocs: false,
      }
      return failed
    }
    return response.json()
  }, [bridge, currentPath, t])

  const toAssistantExtras = useCallback((response: AnyResponse): Partial<Message> => {
    if (isReqGuardResponse(response)) return { blocks: reqGuardResponseBlocks(response), mode: response.mode }
    if (!isAgentResponse(response)) return modeRef.current === 'mock' && serverForcesMock ? { mode: 'mock' } : {}
    const blocks: ChatBlock[] = [
      ...response.rejectedActions.map((r): ChatBlock => ({ kind: 'rejected', name: r.name, reason: r.reason })),
      ...response.cards.map((card): ChatBlock => ({ kind: 'card', card })),
      ...(response.domainKnowledge ? [{ kind: 'knowledge', reference: response.domainKnowledge } satisfies ChatBlock] : []),
      ...response.actions.map((action): ChatBlock => {
        const gate = PROPOSAL_GATES[action.name]
        return gate
          ? { kind: 'confirm', gate, revision: -1, status: 'pending' }
          : { kind: 'action', action, status: 'running' }
      }),
    ]
    return { blocks, mode: response.mode }
  }, [serverForcesMock])

  // The action callbacks are passed *into* the controller but also need its message
  // helpers (which are stable); a ref breaks that cycle.
  const controllerRef = useRef<ReturnType<typeof useChatController<AnyResponse>> | null>(null)

  const setBlock = useCallback((messageId: string, index: number, update: (block: ChatBlock) => ChatBlock) => {
    controllerRef.current?.updateMessage(messageId, (m) => ({
      ...m,
      blocks: m.blocks?.map((b, i) => (i === index ? update(b) : b)),
    }))
  }, [])

  const appendAssistant = useCallback((message: Omit<Message, 'id' | 'timestamp' | 'role'>) => {
    return controllerRef.current!.appendMessage({ role: 'assistant', ...message })
  }, [])

  /**
   * The background requirement check joins the message when ready, collapsed — once per
   * classification, whether it was classified from the Chat or on the page.
   */
  const attachCheckEvidence = useCallback((wizard: WizardBridgeHandle, messageId: string, revision: number) => {
    void wizard.getRequirementCheck().then((evidence) => {
      if (!evidence || revision !== wizard.getSnapshot().classificationRevision) return
      const shown = messagesRef.current.some((m) => m.blocks?.some((b) => b.kind === 'evidence' && b.revision === revision))
      if (shown) return
      controllerRef.current?.updateMessage(messageId, (m) => ({
        ...m,
        blocks: [...(m.blocks ?? []), { kind: 'evidence', evidence, revision }],
      }))
    })
  }, [])

  /** Re-check on the client, against the wizard as it is right now, before touching it. */
  const precheck = useCallback((action: ClientAction, wizard: WizardBridgeHandle | null): string | null => {
    if (!wizard) return t('chat.agent.error.noWizard')
    if (!availableClientTools(wizard.getSnapshot()).includes(action.name)) return t('chat.agent.error.stepChanged')
    if (action.name === 'classify_requirements' && action.source === 'message') {
      const userTexts = messagesRef.current.filter((m) => m.role === 'user').map((m) => m.content)
      if (!userTexts.some((text) => isVerbatimExcerpt(action.requirements, text))) return t('chat.agent.rejected.not_verbatim')
    }
    return null
  }, [t])

  const runAction = useCallback(async (action: ClientAction, messageId: string, index: number) => {
    const wizard = bridge?.getWizard() ?? null
    const blocked = precheck(action, wizard)

    const gate = PROPOSAL_GATES[action.name]
    if (gate) {
      setBlock(messageId, index, () => blocked
        ? { kind: 'confirm', gate, revision: -1, status: 'expired' }
        : {
            kind: 'confirm', gate, revision: wizard!.getSnapshot().classificationRevision, status: 'pending',
            assumptionCount: gate === 'robot' ? selectedAssumptionCount(wizard!.getSnapshot()) : undefined,
          })
      // Classified on the page, not in the Chat: show its evidence with the proposal instead.
      if (!blocked && gate === 'testCases') attachCheckEvidence(wizard!, messageId, wizard!.getSnapshot().classificationRevision)
      return
    }
    if (blocked || !wizard) {
      setBlock(messageId, index, (b) => ({ ...(b as Extract<ChatBlock, { kind: 'action' }>), status: 'failed', detail: blocked ?? '' }))
      return
    }

    setActionRunning(true)
    try {
      switch (action.name) {
        case 'classify_requirements': {
          const result = await wizard.classify(action.source === 'message' ? action.requirements : undefined)
          if (!result.ok) throw new Error(result.error)
          setBlock(messageId, index, (b) => ({
            ...(b as Extract<ChatBlock, { kind: 'action' }>),
            status: 'done',
            detail: t('chat.agent.action.classifyDone', { count: result.requirementCount, category: result.overallCategory }),
          }))
          // Mandatory stop: nothing further happens until a human presses the confirm button.
          appendAssistant({
            content: t('chat.agent.confirm.prompt'),
            blocks: [{ kind: 'confirm', gate: 'testCases', revision: result.revision, status: 'pending' }],
            mode: modeRef.current,
          })
          attachCheckEvidence(wizard, messageId, result.revision)
          break
        }
        case 'regenerate_test_case':
        case 'add_test_case': {
          const result = action.name === 'regenerate_test_case'
            ? await wizard.regenerateTestCase(action.testCaseId, action.feedback)
            : await wizard.addTestCase(action.description)
          if (!result.ok) throw new Error(result.error)
          setBlock(messageId, index, (b) => ({
            ...(b as Extract<ChatBlock, { kind: 'action' }>),
            status: 'done',
            detail: t('chat.agent.action.testCaseDone', { name: result.name }),
          }))
          break
        }
        case 'generate_robot_script': {
          const result = await wizard.generateRobotScript({ feedback: action.feedback })
          if (!result.ok) throw new Error(result.error)
          setBlock(messageId, index, (b) => ({
            ...(b as Extract<ChatBlock, { kind: 'action' }>),
            status: 'done',
            detail: t('chat.agent.action.robotDone'),
          }))
          break
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      setBlock(messageId, index, (b) => ({ ...(b as Extract<ChatBlock, { kind: 'action' }>), status: 'failed', detail: message }))
    } finally {
      setActionRunning(false)
    }
  }, [bridge, precheck, setBlock, appendAssistant, attachCheckEvidence, t])

  /** Runs a Requirement Doctor page action; applying is only ever a confirm card. */
  const runPageAction = useCallback(async (action: ReqGuardAction, messageId: string, index: number) => {
    const page = bridge?.getReqGuard() ?? null
    const userTexts = messagesRef.current.filter((m) => m.role === 'user').map((m) => m.content)
    const blocked = precheckReqGuardAction(action, page, userTexts, t)
    if (action.name === 'propose_apply_coverage') {
      setBlock(messageId, index, () => (blocked || !page
        ? { kind: 'rgConfirm', roundIndex: -1, acceptedCount: 0, status: 'expired' }
        : bindConfirm(page)))
      return
    }
    // Built from the action, not read back from the messages: this runs right after the
    // assistant message was appended, before messagesRef has caught up with it.
    const block: Extract<ChatBlock, { kind: 'rgAction' }> = { kind: 'rgAction', action, status: 'running' }
    if (blocked || !page) {
      setBlock(messageId, index, () => ({ ...block, status: 'failed', detail: blocked ?? '' }))
      return
    }
    setActionRunning(true)
    try {
      const done = await runReqGuardAction(action, page, block, t)
      setBlock(messageId, index, () => done)
    } finally {
      setActionRunning(false)
    }
  }, [bridge, setBlock, t])

  const onResponse = useCallback((response: AnyResponse, assistantMessage: Message) => {
    if (isReqGuardResponse(response)) {
      const index = assistantMessage.blocks?.findIndex((b) => b.kind === 'rgAction' || b.kind === 'rgConfirm') ?? -1
      const action = response.actions[0]
      if (action && index >= 0) void runPageAction(action, assistantMessage.id, index)
      return
    }
    if (!isAgentResponse(response)) return
    const index = assistantMessage.blocks?.findIndex((b) => b.kind === 'action' || b.kind === 'confirm') ?? -1
    const action = response.actions[0]
    if (action && index >= 0) void runAction(action, assistantMessage.id, index)
  }, [runAction, runPageAction])

  const controller = useChatController<AnyResponse>({
    // The Requirement Doctor is a separate project from the vECU test generator (and the
    // docs chat that goes with it): it keeps its own conversation.
    conversationKey: reqGuardActive ? 'req-guard' : 'default',
    sendTurn,
    onIntent: handleIntent,
    toAssistantExtras,
    onResponse,
  })
  controllerRef.current = controller
  messagesRef.current = controller.messages

  /**
   * A human confirmation card was pressed. Each gate runs exactly one wizard step:
   *  - 'testCases': classification review → generate test cases, then stop and offer
   *    the 'robot' gate — whether the cases are good enough is the person's call;
   *  - 'robot': test-case review → generate the Robot script for the selected cases.
   * Test execution stays a button in the wizard.
   */
  const confirmGeneration = useCallback(async (messageId: string, blockIndex: number) => {
    const block = messagesRef.current.find((m) => m.id === messageId)?.blocks?.[blockIndex]
    // Requirement Doctor: the human applies the accepted proposals and re-reviews.
    if (block?.kind === 'rgConfirm') {
      if (block.status !== 'pending') return
      const page = bridge?.getReqGuard() ?? null
      if (isConfirmStale(block, page)) {
        setBlock(messageId, blockIndex, () => ({ ...block, status: 'expired' }))
        return
      }
      setBlock(messageId, blockIndex, () => ({ ...block, status: 'running' }))
      setActionRunning(true)
      try {
        const result = await page!.applyCoverage()
        setBlock(messageId, blockIndex, () => (result.ok
          ? { ...block, status: 'confirmed', summary: result.summary }
          : { ...block, status: 'failed', error: result.error }))
      } finally {
        setActionRunning(false)
      }
      return
    }
    if (!block || block.kind !== 'confirm' || block.status !== 'pending') return

    const wizard = bridge?.getWizard() ?? null
    const snapshot = wizard?.getSnapshot()
    const stale = !wizard || !snapshot || snapshot.busy || snapshot.step !== GATE_STEP[block.gate] ||
      (block.gate === 'testCases' && snapshot.classificationRevision !== block.revision) ||
      (block.gate === 'robot' && snapshot.selectedTestCaseIds.length === 0)
    if (stale) {
      setBlock(messageId, blockIndex, () => ({ ...block, status: 'expired' }))
      return
    }

    setBlock(messageId, blockIndex, () => ({ ...block, status: 'confirmed' }))
    const key = block.gate
    const chainMessage = appendAssistant({
      content: '',
      blocks: [{ kind: 'chain', steps: [{ key, status: 'running' }] }],
      mode: modeRef.current,
    })
    const setChain = (update: Partial<Extract<ChatBlock, { kind: 'chain' }>>) =>
      setBlock(chainMessage.id, 0, (b) => ({ ...(b as Extract<ChatBlock, { kind: 'chain' }>), ...update }))

    setActionRunning(true)
    try {
      if (block.gate === 'testCases') {
        const cases = await wizard.generateTestCases()
        if (!cases.ok) {
          setChain({ steps: [{ key, status: 'failed' }], error: t('chat.agent.chain.failed', { error: cases.error }) })
          return
        }
        if (cases.suspension) {
          setChain({ steps: [{ key, status: 'done' }], detail: t('chat.agent.chain.suspension') })
          return
        }
        setChain({ steps: [{ key, status: 'done' }], detail: t('chat.agent.chain.testCasesDone', { count: cases.testCaseIds.length }) })
        controllerRef.current?.updateMessage(chainMessage.id, (m) => ({
          ...m,
          blocks: [...(m.blocks ?? []), { kind: 'generationEvidence', evidence: cases.evidence }],
        }))
        // Mandatory stop: the person reviews the cases; only their click generates the Robot script.
        appendAssistant({
          content: t('chat.agent.confirm.robotPrompt'),
          blocks: [{ kind: 'confirm', gate: 'robot', revision: snapshot.classificationRevision, status: 'pending', assumptionCount: cases.assumptionCount }],
          mode: modeRef.current,
        })
        return
      }

      const robot = await wizard.generateRobotScript()
      if (!robot.ok) {
        setChain({ steps: [{ key, status: 'failed' }], error: t('chat.agent.chain.failed', { error: robot.error }) })
        return
      }
      setChain({ steps: [{ key, status: 'done' }], detail: t('chat.agent.chain.robotDone', { count: snapshot.selectedTestCaseIds.length }) })
    } finally {
      setActionRunning(false)
    }
  }, [bridge, setBlock, appendAssistant, t])

  // A launch from chat (/auto-test?workflowSessionId=…) opens this chat with a short notice.
  const openRequest = bridge?.openRequest
  const appendMessage = controller.appendMessage
  const hasMessages = controller.messages.length > 0
  const handledOpenSeq = useRef(0)
  useEffect(() => {
    if (!openRequest || openRequest.seq === handledOpenSeq.current) return
    handledOpenSeq.current = openRequest.seq
    if (openRequest.notice && !hasMessages) {
      appendMessage({ role: 'assistant', content: openRequest.notice, blocks: [] })
    }
  }, [openRequest, hasMessages, appendMessage])

  const renderBlocks = useCallback(
    (message: Message) => <AgentBlocks message={message} onConfirm={confirmGeneration} />,
    [confirmGeneration],
  )

  return {
    controller,
    ctaSlot: agentActive ? null : ctaSlot,
    renderBlocks,
    agentActive,
    mode,
    busy: controller.loading || actionRunning,
    loadingLabel: actionRunning ? t(reqGuardActive ? 'chat.reqGuard.working' : 'chat.agent.working') : agentActive ? t('chat.agent.thinking') : undefined,
    /** Names the assistant in the drawer header when it is a separate project's chat. */
    title: reqGuardActive ? t('chat.reqGuard.title') : undefined,
    // On the Requirement Doctor page people often do not know what to ask: show what the Chat can do.
    emptyHint: reqGuardActive ? t('chat.reqGuard.empty.hint') : undefined,
    suggestions: reqGuardActive
      ? [t('chat.reqGuard.empty.sample'), t('chat.reqGuard.empty.review'), t('chat.reqGuard.empty.paste'), t('chat.reqGuard.empty.checklist'), t('chat.reqGuard.empty.knowledge')]
      : undefined,
  }
}
